import { useRef, useCallback, useState } from "react";
import { useCalendarEdit } from "./CalendarEditContext";
import { ShiftValidationError } from "./CalendarEditControls";
import { cn } from "../../lib/utils";
import { GripVertical } from "lucide-react";

/**
 * EditableShiftBlock - A shift block with edit mode affordances
 * Provides visual drag/resize handles and selection states
 * PROMPT 27: Visual-only interactions
 * PROMPT 28: Persistence integration with proper time calculation
 */
export function EditableShiftBlock({
  shiftId,
  shiftData, // Original shift data (required for time calculations)
  children,
  className,
  style,
  onClick,
  colors = {},
  disabled = false,
  "data-testid": testId,
}) {
  const {
    isEditMode,
    isShiftSelected,
    selectShift,
    isDragging,
    isResizing,
    startDrag,
    updateDragPosition,
    endDrag,
    startResize,
    updateResize,
    endResize,
    warnings,
    dragPreview,
    resizePreview,
    getPendingChange,
    getValidationError,
  } = useCalendarEdit();

  const blockRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [localDragOffset, setLocalDragOffset] = useState({ x: 0, y: 0 });
  const [localResizeDelta, setLocalResizeDelta] = useState(0);
  
  const isSelected = isShiftSelected(shiftId);
  const isBeingDragged = isDragging && dragPreview?.shiftId === shiftId;
  const isBeingResized = isResizing && resizePreview?.shiftId === shiftId;
  const pendingChange = getPendingChange(shiftId);
  const validationError = getValidationError(shiftId);
  const hasModification = !!pendingChange;
  const hasError = !!validationError;

  // Handle click
  const handleClick = useCallback((e) => {
    if (isEditMode && !isDragging && !isResizing) {
      e.stopPropagation();
      selectShift(shiftId);
    } else if (onClick && !isEditMode) {
      onClick(e);
    }
  }, [isEditMode, isDragging, isResizing, selectShift, shiftId, onClick]);

  // Handle drag start
  const handleDragStart = useCallback((e) => {
    if (!isEditMode || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    
    const rect = blockRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const initialPosition = {
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
    };
    
    // Pass the shift data for time calculations - use pending modified or original
    const dataToUse = pendingChange?.modified || shiftData;
    
    // Guard: can't drag without shift data
    if (!dataToUse || !dataToUse.start_time || !dataToUse.end_time) {
      return;
    }
    
    startDrag(shiftId, initialPosition, dataToUse);
    
    const startY = e.clientY;
    const startX = e.clientX;
    
    // Track mouse movement
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      setLocalDragOffset({ x: deltaX, y: deltaY });
      
      // Check for warnings
      const newY = initialPosition.y + deltaY;
      const outOfHours = newY < 100 || newY > 800; // Rough bounds
      updateDragPosition(
        { x: initialPosition.x + deltaX, y: newY },
        { outOfHours }
      );
    };
    
    const handleMouseUp = (upEvent) => {
      const deltaY = upEvent.clientY - startY;
      const deltaX = upEvent.clientX - startX;
      
      // Pass the pixel delta to endDrag for time calculation
      endDrag(null, { x: deltaX, y: deltaY });
      setLocalDragOffset({ x: 0, y: 0 });
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [isEditMode, disabled, shiftId, shiftData, pendingChange, startDrag, updateDragPosition, endDrag]);

  // Handle resize start (top edge - adjust start time)
  const handleResizeStartEdge = useCallback((e) => {
    if (!isEditMode || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    
    const rect = blockRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    // Pass the shift data for time calculations
    const dataToUse = pendingChange?.modified || shiftData;
    startResize(shiftId, "start", { top: rect.top, height: rect.height }, dataToUse);
    
    const startY = e.clientY;
    
    const handleMouseMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - startY;
      setLocalResizeDelta(deltaY);
      
      const newTop = rect.top + deltaY;
      const newHeight = rect.height - deltaY;
      
      // Minimum height check
      if (newHeight < 30) return;
      
      const outOfHours = newTop < 100;
      updateResize(
        { top: newTop, height: newHeight },
        { outOfHours, invalidPlacement: newHeight < 30 }
      );
    };
    
    const handleMouseUp = (upEvent) => {
      const deltaY = upEvent.clientY - startY;
      
      // Pass the pixel delta to endResize for time calculation
      endResize(deltaY);
      setLocalResizeDelta(0);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [isEditMode, disabled, shiftId, shiftData, pendingChange, startResize, updateResize, endResize]);

  // Handle resize end (bottom edge - adjust end time)
  const handleResizeEndEdge = useCallback((e) => {
    if (!isEditMode || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    
    const rect = blockRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    // Pass the shift data for time calculations
    const dataToUse = pendingChange?.modified || shiftData;
    startResize(shiftId, "end", { top: rect.top, height: rect.height }, dataToUse);
    
    const startY = e.clientY;
    
    const handleMouseMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - startY;
      setLocalResizeDelta(deltaY);
      
      const newHeight = rect.height + deltaY;
      
      // Minimum height check
      if (newHeight < 30) return;
      
      const outOfHours = (rect.top + newHeight) > 900;
      updateResize(
        { top: rect.top, height: newHeight },
        { outOfHours, invalidPlacement: newHeight < 30 }
      );
    };
    
    const handleMouseUp = (upEvent) => {
      const deltaY = upEvent.clientY - startY;
      
      // Pass the pixel delta to endResize for time calculation
      endResize(deltaY);
      setLocalResizeDelta(0);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [isEditMode, disabled, shiftId, shiftData, pendingChange, startResize, updateResize, endResize]);

  // Compute dynamic styles for drag/resize preview
  const computedStyle = { ...style };
  if (isBeingDragged && (localDragOffset.x !== 0 || localDragOffset.y !== 0)) {
    computedStyle.transform = `translate(${localDragOffset.x}px, ${localDragOffset.y}px)`;
    computedStyle.zIndex = 100;
    computedStyle.opacity = 0.8;
  }
  
  // Apply resize preview
  if (isBeingResized && localResizeDelta !== 0) {
    const currentTop = parseInt(style?.top) || 0;
    const currentHeight = parseInt(style?.height) || 60;
    
    if (resizePreview?.edge === "start") {
      computedStyle.top = `${currentTop + localResizeDelta}px`;
      computedStyle.height = `${currentHeight - localResizeDelta}px`;
    } else {
      computedStyle.height = `${currentHeight + localResizeDelta}px`;
    }
    computedStyle.zIndex = 100;
    computedStyle.opacity = 0.9;
  }

  // Warning styles
  const hasWarning = warnings.overlap || warnings.outOfHours || warnings.invalidPlacement;
  const warningActive = (isBeingDragged || isBeingResized) && hasWarning;

  return (
    <div
      ref={blockRef}
      className={cn(
        "relative group transition-all",
        // Edit mode base styles
        isEditMode && "cursor-pointer",
        isEditMode && !disabled && "hover:ring-2 hover:ring-primary/50 hover:ring-offset-1 hover:ring-offset-transparent",
        // Selected state
        isSelected && "ring-2 ring-primary ring-offset-2 ring-offset-surface-base",
        // Dragging state
        isBeingDragged && "shadow-2xl cursor-grabbing",
        // Resizing state
        isBeingResized && "shadow-lg",
        // Warning state (visual only - non-blocking)
        warningActive && "ring-2 ring-amber-500 ring-offset-1",
        // Modified state (pending change)
        hasModification && !hasError && "ring-2 ring-amber-500/50",
        // Error state (blocking validation error)
        hasError && "ring-2 ring-red-500 ring-offset-1",
        // Disabled in edit mode
        isEditMode && disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      style={computedStyle}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={testId}
      data-edit-mode={isEditMode}
      data-selected={isSelected}
      data-modified={hasModification}
      data-error={hasError}
    >
      {/* Validation error display */}
      {hasError && isEditMode && (
        <ShiftValidationError shiftId={shiftId} />
      )}
      
      {/* Modified indicator badge */}
      {hasModification && !isBeingDragged && !isBeingResized && isEditMode && (
        <div className="absolute -top-2 -right-2 z-20">
          <div className={cn(
            "text-[9px] px-1.5 py-0.5 rounded-full font-semibold shadow-md",
            hasError 
              ? "bg-red-500 text-white" 
              : "bg-amber-500 text-amber-950"
          )}>
            {hasError ? "Error" : "Modified"}
          </div>
        </div>
      )}

      {/* Edit mode affordances - only show in edit mode */}
      {isEditMode && !disabled && (
        <>
          {/* Drag handle - center top */}
          <div
            className={cn(
              "absolute -top-1 left-1/2 -translate-x-1/2 z-10 transition-opacity duration-200",
              (isHovered || isSelected) ? "opacity-100" : "opacity-0"
            )}
            onMouseDown={handleDragStart}
            data-testid={`${testId}-drag-handle`}
          >
            <div className="bg-primary rounded px-1.5 py-0.5 cursor-grab active:cursor-grabbing shadow-md">
              <GripVertical className="h-3 w-3 text-primary-foreground" />
            </div>
          </div>

          {/* Resize handle - top (start time) */}
          <div
            className={cn(
              "absolute top-0 left-0 right-0 h-2 cursor-ns-resize z-10 transition-all",
              "hover:bg-primary/30 rounded-t",
              (isHovered || isSelected) ? "visible" : "invisible"
            )}
            onMouseDown={handleResizeStartEdge}
            data-testid={`${testId}-resize-top`}
          >
            <div className="absolute top-0.5 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary/50 rounded" />
          </div>

          {/* Resize handle - bottom (end time) */}
          <div
            className={cn(
              "absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize z-10 transition-all",
              "hover:bg-primary/30 rounded-b",
              (isHovered || isSelected) ? "visible" : "invisible"
            )}
            onMouseDown={handleResizeEndEdge}
            data-testid={`${testId}-resize-bottom`}
          >
            <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary/50 rounded" />
          </div>

          {/* Selection indicator */}
          {isSelected && (
            <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-full" />
          )}
        </>
      )}

      {/* Warning indicator - only during active drag/resize */}
      {warningActive && (
        <div className="absolute -top-2 -right-2 z-20">
          <div className="bg-amber-500 text-amber-950 text-[9px] px-1.5 py-0.5 rounded-full font-semibold shadow-md">
            {warnings.overlap && "Overlap"}
            {warnings.outOfHours && "Out of hours"}
            {warnings.invalidPlacement && "Invalid"}
          </div>
        </div>
      )}

      {/* Original content */}
      {children}
    </div>
  );
}

export default EditableShiftBlock;
